import React, { useState } from 'react';
import {
  IconClipboardCheck,
  IconExternalLink,
  IconRefresh,
  IconX,
  IconLoader2,
  IconAlertTriangle,
} from '@tabler/icons-react';
import type { WorkItem } from '../workitems/workItemTypes';
import { useCheckInDeviceIdentities, formatDeviceIdentityParts } from '../../hooks/useCheckInDeviceIdentities';
import { Chip, toneFromMuiColor } from '../chip';
import {
  intakeTypeLabel,
  paymentLabel,
  paymentChipColor,
  formatAud,
} from '../check-in/sessionDetail/sessionDetailHelpers';
// Pulls in .panel-section-header + the .work-item-expanded--checkin variant tint
// the header band reuses.
import './WorkItemPanel.css';
import './CheckInPanelSection.css';

interface CheckInPanelSectionProps {
  workItem: WorkItem;
  /** Open the full check-in session detail view. */
  onOpenCheckInSession?: (sessionId: string) => void;
  /** Cancel the check-in session (opens the dashboard's CancelSessionDialog). */
  onDelete?: (workItem: WorkItem) => Promise<void>;
  /** Optimistically patch the row after a retry-orchestration succeeds. */
  onWorkItemUpdated: (id: string, updates: Partial<WorkItem>) => void;
}

const INSPECTION_LABEL: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

const INSPECTION_TONE: Record<string, 'neutral' | 'warning' | 'success'> = {
  not_started: 'neutral',
  in_progress: 'warning',
  complete: 'success',
};

/** "hard_drive" → "Hard drive". */
const formatDeviceType = (deviceType: string): string => {
  const spaced = deviceType.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Expiry timestamp → "19 May 2026, 8:48 am". */
const formatExpiry = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Check-in session details for the V2 technician-card expansion panel — a white
 * design-system card matching the Customer / Job-log cards beside it: a section
 * header (icon + title + Open Session), intake / payment / attention chips,
 * expiry + preliminary-charges facts, the draft-device list, retry orchestration,
 * and cancel (routes to the dashboard's cancel dialog).
 */
export const CheckInPanelSection: React.FC<CheckInPanelSectionProps> = ({
  workItem,
  onOpenCheckInSession,
  onDelete,
  onWorkItemUpdated,
}) => {
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Per-device make/serial — fetched from the full session (not on the summary).
  const { identities } = useCheckInDeviceIdentities(workItem);

  const handleRetry = async () => {
    if (!workItem.checkInSessionGuid) return;
    setRetrying(true);
    try {
      const response = await fetch(
        `/api/check-in-sessions/${workItem.checkInSessionGuid}/orchestration/retry`,
        { method: 'POST' },
      );
      if (!response.ok) throw new Error(`Retry failed: ${response.status}`);
      onWorkItemUpdated(workItem.id, { checkInRetryAvailable: false, checkInRequiresAttention: false });
    } catch (err) {
      console.error('[CheckIn] Retry orchestration failed:', err);
    } finally {
      setRetrying(false);
    }
  };

  const handleCancel = async () => {
    if (!onDelete) return;
    setCancelling(true);
    try {
      await onDelete(workItem);
    } finally {
      setCancelling(false);
    }
  };

  const canRetry = !!(workItem.checkInRetryAvailable && workItem.checkInSessionGuid);
  const draftJobs = workItem.checkInDraftJobs ?? [];

  return (
    <div className="checkin-panel-wrap">
      <div className="checkin-panel">
        <div className="panel-section-header">
          <IconClipboardCheck className="panel-section-header__icon" size={18} stroke={1.5} />
          <span className="panel-section-header__title">Check-In Session</span>
          {onOpenCheckInSession && workItem.checkInSessionGuid && (
            <button
              type="button"
              className="checkin-btn checkin-btn--primary checkin-btn--sm"
              onClick={() => onOpenCheckInSession(workItem.checkInSessionGuid!)}
            >
              <IconExternalLink size={14} stroke={1.5} />
              Open Session
            </button>
          )}
        </div>

        <div className="checkin-panel__body">
          {(workItem.checkInIntakeType || workItem.checkInPaymentState || workItem.checkInRequiresAttention) && (
            <div className="checkin-panel__chips">
              {workItem.checkInIntakeType && (
                <Chip tone="neutral">{intakeTypeLabel(workItem.checkInIntakeType)}</Chip>
              )}
              {workItem.checkInPaymentState && (
                <Chip tone={toneFromMuiColor(paymentChipColor(workItem.checkInPaymentState))}>
                  {paymentLabel(workItem.checkInPaymentState)}
                </Chip>
              )}
              {workItem.checkInRequiresAttention && (
                <Chip tone="error">
                  <IconAlertTriangle size={12} stroke={1.5} />
                  Needs attention
                </Chip>
              )}
            </div>
          )}

          {(workItem.checkInExpiresAt || typeof workItem.checkInPreliminaryChargesTotal === 'number') && (
            <div className="checkin-panel__facts">
              {workItem.checkInExpiresAt && (
                <div className="checkin-fact">
                  <span className="checkin-fact__label">Expires</span>
                  <span className="checkin-fact__value">{formatExpiry(workItem.checkInExpiresAt)}</span>
                </div>
              )}
              {typeof workItem.checkInPreliminaryChargesTotal === 'number' && (
                <div className="checkin-fact">
                  <span className="checkin-fact__label">Preliminary charges</span>
                  <span className="checkin-fact__value checkin-fact__value--amount">
                    {formatAud(workItem.checkInPreliminaryChargesTotal)}
                  </span>
                </div>
              )}
            </div>
          )}

          {draftJobs.length > 0 && (
            <div className="checkin-panel__devices">
              <span className="checkin-panel__subhead">
                {draftJobs.length} {draftJobs.length === 1 ? 'Device' : 'Devices'}
              </span>
              {draftJobs.map(dj => {
                const identityParts = formatDeviceIdentityParts(identities[dj.id], dj.deviceType);
                return (
                <div className="checkin-device" key={dj.id}>
                  <div className="checkin-device__body">
                    <div className="checkin-device__main">
                      <Chip tone="neutral">{formatDeviceType(dj.deviceType)}</Chip>
                      <span className="checkin-device__summary" title={dj.serviceSummary}>
                        {dj.serviceSummary}
                      </span>
                      {(dj.repairShoprTicketNumber || dj.repairShoprTicketId) && (
                        <Chip tone="info">RS #{dj.repairShoprTicketNumber ?? dj.repairShoprTicketId}</Chip>
                      )}
                    </div>
                    {identityParts.length > 0 && (
                      <span className="checkin-device__identity" title={identityParts.join(' · ')}>
                        {identityParts.join(' · ')}
                      </span>
                    )}
                  </div>
                  <div className="checkin-device__end">
                    <Chip tone={INSPECTION_TONE[dj.inspectionStatus] ?? 'neutral'}>
                      {INSPECTION_LABEL[dj.inspectionStatus] ?? dj.inspectionStatus}
                    </Chip>
                    <span className="checkin-device__charge">{formatAud(dj.chargesTotal ?? 0)}</span>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {(canRetry || onDelete) && (
            <div className={`checkin-panel__actions${canRetry && onDelete ? ' checkin-panel__actions--split' : ''}`}>
              {canRetry && (
                <button
                  type="button"
                  className="checkin-btn checkin-btn--warning"
                  onClick={handleRetry}
                  disabled={retrying}
                >
                  {retrying ? (
                    <IconLoader2 size={15} stroke={1.5} className="checkin-spin" />
                  ) : (
                    <IconRefresh size={15} stroke={1.5} />
                  )}
                  {retrying ? 'Retrying…' : 'Retry orchestration'}
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="checkin-btn checkin-btn--danger"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? (
                    <IconLoader2 size={15} stroke={1.5} className="checkin-spin" />
                  ) : (
                    <IconX size={15} stroke={1.5} />
                  )}
                  {cancelling ? 'Cancelling…' : 'Cancel session'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
